import { useCallback, useState } from "react";
import type { NextPage } from "next";
import { UnauthedLayout } from "@/layouts";
import { SuccessScreen } from "./__components__/success-screen";
import {
  PasswordChangeForm,
  type PasswordChangeFormValues,
  defaultValuesPasswordChange,
} from "./__components__/password-change-form";
import { FormProvider, useForm } from "react-hook-form";
import { useApolloError } from "@/hooks/useApolloError";
import { useMePagesSettingsAccountChangePasswordQuery } from "./__gql__/me.gen";
import { useDoRenewPasswordPagesSettingsAccountChangePasswordMutation } from "./__gql__/do-renew-password.gen";
import { useVerifyPasswordPagesSettingsAccountChangePasswordMutation } from "./__gql__/verify-password.gen";
import { noop } from "@/modules/utils/function";
import { NextSeo } from "next-seo";
import { apolloErrorDoRenewPassword, apolloErrorMe, apolloErrorVerifyPassword } from "./const";

const SettingsAccountChangePasswordPage: NextPage = () => {
  const resultMePagesSettingsAccountChangePasswordQuery = useMePagesSettingsAccountChangePasswordQuery();

  const [
    verifyPasswordPagesSettingsAccountChangePasswordMutation,
    resultVerifyPasswordPagesSettingsAccountChangePasswordMutation,
  ] = useVerifyPasswordPagesSettingsAccountChangePasswordMutation();
  const [
    doRenewPasswordPagesSettingsAccountChangePasswordMutation,
    resultDoRenewPasswordPagesSettingsAccountChangePasswordMutation,
  ] = useDoRenewPasswordPagesSettingsAccountChangePasswordMutation();

  useApolloError(resultMePagesSettingsAccountChangePasswordQuery.error, {
    testid: apolloErrorMe,
  });
  useApolloError(resultVerifyPasswordPagesSettingsAccountChangePasswordMutation.error, {
    testid: apolloErrorVerifyPassword,
  });
  useApolloError(resultDoRenewPasswordPagesSettingsAccountChangePasswordMutation.error, {
    testid: apolloErrorDoRenewPassword,
  });

  const loading =
    resultVerifyPasswordPagesSettingsAccountChangePasswordMutation.loading ||
    resultDoRenewPasswordPagesSettingsAccountChangePasswordMutation.loading;

  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);

  const methods = useForm<PasswordChangeFormValues>({
    defaultValues: defaultValuesPasswordChange,
    mode: "all",
    criteriaMode: "all",
  });
  const { handleSubmit } = methods;

  const handleChangePassword = useCallback(
    async ({ password, newPassword }: PasswordChangeFormValues): Promise<void> => {
      verifyPasswordPagesSettingsAccountChangePasswordMutation({
        variables: {
          password,
        },
      })
        .then(({ data }) => {
          if (data?.verifyPassword === true) {
            return doRenewPasswordPagesSettingsAccountChangePasswordMutation({
              variables: {
                newPassword,
              },
            });
          }
          return Promise.reject(new Error("invalid password"));
        })
        .then(({ data }) => {
          if (data?.doRenewPassword === true) {
            setIsSubmitSuccessful(true);
          }
        })
        .catch(noop);
    },
    [
      verifyPasswordPagesSettingsAccountChangePasswordMutation,
      doRenewPasswordPagesSettingsAccountChangePasswordMutation,
    ],
  );

  return (
    <>
      <NextSeo title="パスワードの変更" description="パスワードの変更が行えます。" />
      <UnauthedLayout pt="1.5rem" px="1rem" bgColor="light.main">
        {!isSubmitSuccessful ? (
          <FormProvider {...methods}>
            <form
              onSubmit={(e) => {
                // biome-ignore lint/complexity/noVoid :[AUTOMATICALLY-INSERTED] The use of void is not allowed.
                void handleSubmit(handleChangePassword)(e);
              }}
            >
              <PasswordChangeForm
                email={resultMePagesSettingsAccountChangePasswordQuery.data?.me.mail}
                loading={loading}
              />
            </form>
          </FormProvider>
        ) : (
          <SuccessScreen />
        )}
      </UnauthedLayout>
    </>
  );
};

export default SettingsAccountChangePasswordPage;
